import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { signInWithPopup, signInWithPhoneNumber } from 'firebase/auth';
import { auth, googleProvider, facebookProvider, getFCMToken, setupRecaptcha } from '../../lib/firebase';
import { useUserSignupMutation } from '../../features/api/apiSlice';
import { useDispatch } from 'react-redux';
import { setCredentials } from '../../features/auth/authSlice';
import Header from '../../components/ui/Header';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import SocialLogin from './components/SocialLogin';
import SecurityFeatures from './components/SecurityFeatures';
import RegistrationPrompt from './components/RegistrationPrompt';
import Icon from '../../components/AppIcon';
import { useToast } from '../../components/ui/Toast';
import {
  PORTAL,
  assertPortalAccess,
  buildAuthUserPayload,
  CUSTOMER_ROLE,
} from '../../lib/authPortals';
import AuthPortalSwitcher from '../../components/ui/AuthPortalSwitcher';
import ProfessionalInviteBanner from '../../components/ui/ProfessionalInviteBanner';
import PasswordLoginForm from '../../components/auth/PasswordLoginForm';

/** User login only (property seekers). Professionals use /pro/login. */
const Login = () => {
  const [isLoading, setIsLoading] = useState(false);
  // const [error, setError] = useState(''); // Removed local error state
  const [showCaptcha, setShowCaptcha] = useState(false);
  const [captchaVerified, setCaptchaVerified] = useState(false);
  const [loginAttempts, setLoginAttempts] = useState(0);
  const [authMethod, setAuthMethod] = useState('password'); // 'password' | 'social' | 'phone'
  const [phoneNumber, setPhoneNumber] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [confirmationResult, setConfirmationResult] = useState(null);
  const [showVerification, setShowVerification] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [userSignup] = useUserSignupMutation();
  const dispatch = useDispatch();
  const addToast = useToast(); // Initialize useToast

  const defaultRedirect = location.state?.from?.pathname || '/user-dashboard';

  const completeCustomerAuth = async (user, type, extra = {}) => {
    const loc = await getUserLocation();
    const fcmToken = await getFCMToken();

    const response = await userSignup({
      type,
      firebase_id: user.uid,
      name: user.displayName || user.phoneNumber || user.email?.split('@')[0] || 'User',
      email: user.email || '',
      mobile: user.phoneNumber || '',
      fcm_id: fcmToken || '',
      latitude: loc.latitude,
      longitude: loc.longitude,
      portal: 'customer',
      account_role: CUSTOMER_ROLE,
      ...extra,
    }).unwrap();

    const apiUser = response.user || response.data || {};
    // Existing pros who sign in here keep their pro role from the API
    const authUser = buildAuthUserPayload({ apiUser, firebaseUser: user });
    const access = assertPortalAccess(authUser.account_role, PORTAL.CUSTOMER);

    dispatch(
      setCredentials({
        user: authUser,
        token: response.token,
        customerId: response.customerId || apiUser.id,
      })
    );

    if (access.isProfessionalOnCustomerPortal) {
      addToast('Welcome back — opening your Agent Dashboard.', 'success');
    } else {
      addToast('Login successful!', 'success');
    }

    const target =
      access.redirect && access.isProfessionalOnCustomerPortal
        ? access.redirect
        : defaultRedirect !== '/user-dashboard'
          ? defaultRedirect
          : access.redirect || '/user-dashboard';
    window.location.href = target;
  };

  useEffect(() => {
    // Show captcha after 2 failed attempts
    if (loginAttempts >= 2) {
      setShowCaptcha(true);
    }
  }, [loginAttempts]);

  useEffect(() => {
    // Initialize reCAPTCHA verifier once when the component mounts
    // This ensures the reCAPTCHA element is present and initialized correctly
    setupRecaptcha('recaptcha-container');
  }, []);

  // Function to get user location
  const getUserLocation = () => {
    return new Promise((resolve) => {
      if (!navigator.geolocation) {
        resolve({ latitude: null, longitude: null });
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            latitude: position.coords.latitude.toString(),
            longitude: position.coords.longitude.toString()
          });
        },
        (error) => {
          console.error('Error getting location:', error);
          addToast('Failed to get your location.', 'error'); // Use toast for error
          resolve({ latitude: null, longitude: null });
        },
        { timeout: 10000 }
      );
    });
  };

  const handleSocialLogin = async (provider) => {
    setIsLoading(true);
    try {
      const authProvider = provider === 'google' ? googleProvider : facebookProvider;
      const result = await signInWithPopup(auth, authProvider);
      await completeCustomerAuth(result.user, provider);
    } catch (err) {
      console.error('Social login error:', err);
      addToast(`${provider} login failed. Please try again.`, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCaptchaVerify = (verified) => {
    setCaptchaVerified(verified);
  };

  const handlePhoneAuth = async () => {
    if (!phoneNumber.trim()) {
      addToast('Please enter a phone number', 'error'); // Use toast for error
      return;
    }

    setIsLoading(true);
    // setError(''); // Removed local error state

    try {
      // Use the already setup reCAPTCHA verifier
      const recaptchaVerifier = window.recaptchaVerifier;
      if (!recaptchaVerifier) {
        addToast('reCAPTCHA not initialized. Please refresh the page.', 'error');
        setIsLoading(false);
        return;
      }

      // Send verification code
      const confirmation = await signInWithPhoneNumber(auth, phoneNumber, recaptchaVerifier);
      setConfirmationResult(confirmation);
      setShowVerification(true);
      addToast('Verification code sent to your phone.', 'info'); // Show info toast
    } catch (err) {
      console.error('Phone auth error:', err);
      addToast('Failed to send verification code. Please try again.', 'error'); // Use toast for error
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    if (!verificationCode.trim()) {
      addToast('Please enter the verification code', 'error'); // Use toast for error
      return;
    }

    setIsLoading(true);
    // setError(''); // Removed local error state

    try {
      const result = await confirmationResult.confirm(verificationCode);
      await completeCustomerAuth(result.user, 'phone');
    } catch (err) {
      console.error('Code verification error:', err);
      addToast('Invalid verification code. Please try again.', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="pt-16">
        <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-4 sm:px-6 lg:px-8">
          <div className="max-w-md w-full space-y-6">
            {/* Header */}
            <div className="text-center">
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 bg-primary rounded-xl flex items-center justify-center">
                  <Icon name="Home" size={32} color="white" />
                </div>
              </div>
              
              <h1 className="text-3xl font-bold text-foreground mb-2">
                User Login
              </h1>
              <p className="text-muted-foreground">
                Sign in to search properties, save favorites, and post what you need
              </p>
            </div>

            <AuthPortalSwitcher mode="login" active="customer" />

            {/* Login Form Card */}
            <div className="bg-card border border-border rounded-lg shadow-lg p-6 sm:p-8">
              {/* Auth Method Tabs */}
              <div className="flex border-b border-border mb-6 overflow-x-auto">
                <button
                  type="button"
                  onClick={() => setAuthMethod('password')}
                  className={`flex-1 py-2 px-3 text-center text-sm font-medium whitespace-nowrap transition-colors ${
                    authMethod === 'password'
                      ? 'text-primary border-b-2 border-primary'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  Email &amp; Password
                </button>
                <button
                  type="button"
                  onClick={() => setAuthMethod('social')}
                  className={`flex-1 py-2 px-3 text-center text-sm font-medium whitespace-nowrap transition-colors ${
                    authMethod === 'social'
                      ? 'text-primary border-b-2 border-primary'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  Social
                </button>
                <button
                  type="button"
                  onClick={() => setAuthMethod('phone')}
                  className={`flex-1 py-2 px-3 text-center text-sm font-medium whitespace-nowrap transition-colors ${
                    authMethod === 'phone'
                      ? 'text-primary border-b-2 border-primary'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  Phone
                </button>
              </div>

              {authMethod === 'password' && (
                <PasswordLoginForm
                  portal="customer"
                  redirectPath={defaultRedirect}
                  registerPath="/register"
                />
              )}

              {authMethod === 'social' && (
                <>
                  <SocialLogin
                    onSocialLogin={handleSocialLogin}
                    isLoading={isLoading}
                  />
                  <div className="mt-6">
                    <SecurityFeatures
                      showCaptcha={showCaptcha}
                      onCaptchaVerify={handleCaptchaVerify}
                    />
                  </div>
                </>
              )}

              {authMethod === 'phone' && (
                <div className="space-y-4">
                  {!showVerification ? (
                    <>
                      <Input
                        label="Phone Number"
                        type="tel"
                        placeholder="+256 7XX XXX XXX"
                        value={phoneNumber}
                        onChange={(e) => setPhoneNumber(e.target.value)}
                        required
                        disabled={isLoading}
                      />
                      <Button
                        type="button"
                        variant="default"
                        size="lg"
                        fullWidth
                        onClick={handlePhoneAuth}
                        loading={isLoading}
                        disabled={isLoading}
                      >
                        Send Verification Code
                      </Button>
                    </>
                  ) : (
                    <>
                      <Input
                        label="Verification Code"
                        type="text"
                        placeholder="Enter 6-digit code"
                        value={verificationCode}
                        onChange={(e) => setVerificationCode(e.target.value)}
                        required
                        disabled={isLoading}
                      />
                      <Button
                        type="button"
                        variant="default"
                        size="lg"
                        fullWidth
                        onClick={handleVerifyCode}
                        loading={isLoading}
                        disabled={isLoading}
                      >
                        Verify Code
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        fullWidth
                        onClick={() => {
                          setShowVerification(false);
                          setVerificationCode('');
                          setConfirmationResult(null);
                        }}
                        disabled={isLoading}
                      >
                        Back to Phone Number
                      </Button>
                    </>
                  )}
                </div>
              )}
              <div id="recaptcha-container" style={{ position: 'absolute', left: '-9999px' }}></div>
            </div>

            {/* Registration Prompt */}
            <RegistrationPrompt isLoading={isLoading} />

            <ProfessionalInviteBanner compact />

            {/* Privacy Notice */}
            <div className="text-center">
              <p className="text-xs text-muted-foreground">
                By signing in, you agree to our{' '}
                <Link to="/terms" className="text-primary hover:underline">
                  Terms of Service
                </Link>{' '}
                and{' '}
                <Link to="/privacy" className="text-primary hover:underline">
                  Privacy Policy
                </Link>
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Login;
